import dayjs from 'dayjs';
import _flatMap from 'lodash/flatMap';
import { CSV_COLUMNS, ERASED_KEY_STATUSES, EXPORT_FORMAT, KEY_STATUS, KEY_STATUS_META } from '../constants';
import { DpdpaSource, ExportFormat, UserData } from '../types';

// Cells starting with these characters are executed as formulas by spreadsheet apps. `+` and `-` are
// deliberately left out: every phone number starts with +91, and a legal export must keep values exact.
const FORMULA_PREFIX = /^[=@\t\r]/;

const escapeCsvCell = (raw: unknown): string => {
	if (raw === null || raw === undefined) return '""';
	let value: string = String(raw);
	if (FORMULA_PREFIX.test(value)) value = `'${value}`;
	return `"${value.replace(/"/g, '""')}"`;
};

export const buildJson = (data: UserData, ticketId: string, exportedAt: string): string =>
	JSON.stringify({ ...data, ticketId, exportedAt }, null, 2);

const RECORD_SOURCE = 'record';

// Rows share the field columns: source, group, field, label, value, then three blanks
const contextRow = (source: string, field: string, label: string, value: unknown): unknown[] => [
	source,
	'Context',
	field,
	label,
	value,
	'',
	'',
	''
];

const erasureText = (data: UserData): string => {
	if (!ERASED_KEY_STATUSES.includes(data.keyStatus)) return '';
	const by: string = data.erasure?.by || (data.keyStatus === KEY_STATUS.ERASED_BY_LEGAL ? 'legal' : 'user');
	const date: string = data.erasure?.date ? ` on ${data.erasure.date}` : '';
	return `Erased by ${by}${date}; encrypted fields are unrecoverable and are not included`;
};

const recordRows = (data: UserData, ticketId: string, exportedAt: string): unknown[][] => [
	contextRow(RECORD_SOURCE, 'uid', 'Zupee user ID', data.uid),
	contextRow(RECORD_SOURCE, 'ticketId', 'Ticket ID', ticketId),
	contextRow(RECORD_SOURCE, 'fetchedAt', 'Fetched at', data.fetchedAt),
	contextRow(RECORD_SOURCE, 'exportedAt', 'Exported at', exportedAt),
	contextRow(RECORD_SOURCE, 'keyStatus', 'Key status', KEY_STATUS_META[data.keyStatus]?.label || data.keyStatus),
	contextRow(RECORD_SOURCE, 'erasure', 'Erasure', erasureText(data)),
	...(data.warnings || []).map((warning) => contextRow(RECORD_SOURCE, 'warning', 'Warning', warning))
];

const sourceRows = (source: DpdpaSource, isErased: boolean): unknown[][] => {
	const name: string = source.label || source.table;
	const context: unknown[][] = [
		contextRow(name, 'table', 'Table', source.table),
		contextRow(name, 'rowFound', 'Record found', source.rowFound ? 'Yes' : 'No record for this user'),
		contextRow(name, 'dataAsOf', 'Data as of', source.dataAsOf)
	];
	// Same as the page: an erased user's encrypted fields are unrecoverable, so only plain fields are listed
	const fields: unknown[][] = _flatMap(source.groups || [], (group) =>
		(group.fields || [])
			.filter((field) => !(isErased && field.encrypted))
			.map((field) => [
				name,
				group.group,
				field.key,
				field.label,
				field.value,
				field.encrypted,
				field.redacted,
				field.error || ''
			])
	);
	return [...context, ...fields];
};

export const buildCsv = (data: UserData, ticketId: string, exportedAt: string): string => {
	const isErased: boolean = ERASED_KEY_STATUSES.includes(data.keyStatus);
	const rows: unknown[][] = [
		...recordRows(data, ticketId, exportedAt),
		..._flatMap(data.sources || [], (source) => sourceRows(source, isErased))
	];
	const lines: string[] = [CSV_COLUMNS, ...rows].map((row) => row.map(escapeCsvCell).join(','));
	// BOM so spreadsheet apps read non-ASCII names as UTF-8
	return `\uFEFF${lines.join('\r\n')}`;
};

export const buildFileName = (uid: string, format: ExportFormat): string => `dpdpa_${uid}_${dayjs().format('YYYYMMDD')}.${format}`;

export const downloadExport = (data: UserData, ticketId: string, format: ExportFormat): void => {
	const exportedAt: string = new Date().toISOString();
	const isJson: boolean = format === EXPORT_FORMAT.JSON;
	const content: string = isJson ? buildJson(data, ticketId, exportedAt) : buildCsv(data, ticketId, exportedAt);
	const blob = new Blob([content], { type: isJson ? 'application/json;charset=utf-8;' : 'text/csv;charset=utf-8;' });
	const url: string = window.URL.createObjectURL(blob);
	const link: HTMLAnchorElement = document.createElement('a');
	link.setAttribute('download', buildFileName(data.uid, format));
	link.href = url;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	window.URL.revokeObjectURL(url);
};
